import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { serverHost } from "../../../../http_common.js";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./pointOfSalesLogin.css";
import * as ActionHybrid from "../../../../store/redux/terminal_reducer.js";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import IsLoading from "../../../../IsLoading.jsx";
import AlertBar from "../../../../AlertBar.jsx";

import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { ConstructionOutlined } from "@mui/icons-material";



export default function PointOfSaleLogin({ setTemporaryAuth }) {
  const [alert, setAlert] = useState(null);
  const { accessType } = useSelector((state) => state.clientFunction);
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const openRequestRef = useRef(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState({});
  const [inventoriesPayload, setInventoriesPayload] = useState([]);
  const [inventoryError, setInventoryError] = useState("");
  const [currentPage, seCurrentPage] = useState("");

  const companyData = useSelector(
    (state) => state.clientFunction?.companiesDB?.clientData,
  ); 
  const staffData = useSelector(
    (state) => state.clientFunction?.companiesDB?.staffData,
  );

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const updatedPin = [...pin];
    updatedPin[index] = value;

    setPin(updatedPin);

    // Move to next input
    if (value && index < pin.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Move back on delete
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };
  const { registerData: savedRegister, lastRegisterClosure, inventoryPOS, on_holded_sales } = useSelector(
    state => state.terminalActions,
  );
  const [registerIdentity] = useState(() => {
    const d = new Date();
    const YY = String(d.getFullYear()).slice(-2);
    const MM = String(d.getMonth() + 1).padStart(2, "0");
    const DD = String(d.getDate()).padStart(2, "0");
    const HH = String(d.getHours()).padStart(2, "0");
    const MIN = String(d.getMinutes()).padStart(2, "0");
    const SS = String(d.getSeconds()).padStart(2, "0");
    const random = window.crypto?.randomUUID?.().replace(/-/g, "").slice(0, 16) || Math.random().toString(36).slice(2);
    return `REG${companyData?.clientId?.slice(-7)}-${YY}${MM}${DD}-${HH}${MIN}${SS}-${random}`;
  });

  useEffect(() => {
    if (savedRegister?.requiresLogin) {
      setSelectedInventory(savedRegister.inventory || {});
      setPin(["", "", "", "", "", ""]);
    seCurrentPage("pin-page");
      setAlert({ type: "info", message: "Verify your POS PIN to record this existing register. Your basket and held sales have been retained." });
    } else if (lastRegisterClosure?.clientId === companyData?.clientId) {
      setAlert({ type: "success", message: `Register ${lastRegisterClosure.registerId} closed. Sales: ${lastRegisterClosure.summary?.sales || 0}; cash sales: ${lastRegisterClosure.summary?.cash || 0}.` });
    }
  }, [savedRegister?.requiresLogin, lastRegisterClosure?.registerId, companyData?.clientId]);

  const apiFetchItems = async () => {
    if (openRequestRef.current) return;
    if (!selectedInventory?.inventoryId || !/^\d{6}$/.test(pin.join(""))) {
      setAlert({ type: "warning", message: "Select an inventory and enter your six-digit POS PIN." });
      return;
    }
    if ((inventoryPOS?.cart?.length || on_holded_sales?.length) && savedRegister?.inventory?.inventoryId !== selectedInventory.inventoryId) {
      setAlert({ type: "warning", message: "Resume the original inventory to retain your unfinished basket and held sales." });
      return;
    }
    const requestRegisterId = savedRegister?.requiresLogin && savedRegister.inventory?.inventoryId === selectedInventory.inventoryId
      ? savedRegister.registerId : registerIdentity;
    openRequestRef.current = true;
    setLoading(true);
    let registerOpened = false;
    // Fullscreen must be requested inside the cashier's click/Enter gesture.
    const wasFullscreen = Boolean(document.fullscreenElement);
    let fullscreenRequest = Promise.resolve(false);
    if (!wasFullscreen && document.documentElement.requestFullscreen) {
      try {
        fullscreenRequest = Promise.resolve(document.documentElement.requestFullscreen())
          .then(() => true, () => false);
      } catch (_) { /* Authentication continues if fullscreen is denied. */ }
    }
    await axios.get(
  `${serverHost}/pointOfsale-login/fetch-inventory-items/client/${companyData?.clientId}?inventoryId=${selectedInventory?.inventoryId}&pin=${pin.join("")}&accessType=${accessType}&userId=${staffData?.userId || ""}&registerId=${encodeURIComponent(requestRegisterId)}`
)
      .then((response) => {
        const itemsPayload = response.data?.productDB; 
        if (response?.data?.status === 201) {
          if (itemsPayload !== undefined && itemsPayload !== null && response.data?.registerData?.registerId === requestRegisterId) { 
            registerOpened = true;
            dispatch(
              ActionHybrid.terminalAction({ itemsPayload, registerData: response.data.registerData }),
            );
            navigate(
              `/clients/point-of-sale/workspace/inventory-transactions`,
            );
          } else {
            setAlert({
              message: "Register opening could not be verified. Retry with the same register ID.",
              type: "error",
            });
          }
        } else if (response?.data?.status === 201 && itemsPayload.length == 0) {
          setAlert({
            message:
              "Your inventory is empty, please add items or products to your inventory to have access to Point of Sales",
            type: "info",
          });
        } else {
          setInventoryError(response.data?.message || "Unable to load inventories.");
        }

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.code === "ERR_NETWORK") {
          setAlert({
            message: `${err.message} : please connect to the internet and try again`,
            type: "error",
          });
        } else {
          setAlert({
            message: err.response?.data?.message || err.message,
            type: "error",
          });
        }
        console.log(err?.code || "REQUEST_FAILED");
      })
      .finally(async () => {
        openRequestRef.current = false;
        if (!registerOpened && !wasFullscreen && await fullscreenRequest &&
            document.fullscreenElement === document.documentElement && document.exitFullscreen) {
          try { await document.exitFullscreen(); }
          catch (_) { /* Fullscreen restoration is best-effort. */ }
        }
      });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      apiFetchItems();
    }
  };

  const apiFetchInventories = async () => {
    setLoading(true);
    setInventoriesPayload([]);
    setInventoryError("");

    await axios
      .get(
        `${serverHost}/conditionalInventoryFetch/get-inventories/${companyData?.clientId}?inventoryId=${selectedInventory?.inventoryId}`,
      )
      .then((response) => {
        const fetchedInventories = response.data?.inventoriesData; 
        if (response?.data?.status === 200) {
          if (Array.isArray(fetchedInventories)) {
            setInventoriesPayload(fetchedInventories);
            setInventoryError("");
          } else {
            setInventoryError(response.data?.message || "Invalid inventory response.");
          }
        } else {
          setAlert({
            message: response.data?.message,
            type: "warning",
          });
        }

        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);

        if (err.code === "ERR_NETWORK") {
          setAlert({
            message: `${err.message} : please connect to the internet and try again`,
            type: "error",
          });
        } else {
          setAlert({
            message: `${err.message}`,
            type: "error",
          });
        }

        setInventoryError(err.response?.data?.message || "Could not connect to the inventory service.");
      });
  };

  useEffect(() => {
    if (companyData?.clientId) {
      apiFetchInventories();
    }
  }, [companyData?.clientId]);
  return (
    <>
      {alert && <AlertBar alert={alert} setAlert={setAlert} />}
      {loading && <IsLoading />}
      {currentPage === "pin-page" ? (
        <div className="pointOfSaleAuthContWrap fx-ac fx-jc pdlarge pos-saas-login">
          <div className="pointOfSaleAuthCont fx-cl fx-ac fx-jc space2 pos-saas-auth">
            <div className="fx-cl space1">
              <div className="fx-cl">
                <span>
                  <PointOfSaleIcon
                    style={{ fontSize: "5.4rem", color: "#0354d5" }}
                  />
                </span>
                <span className="pos-login-eyebrow">SECURE REGISTER ACCESS</span>
                <span className="fw600 fs7">Open Commerce Terminal</span>
                <span className="pos-login-inventory">{selectedInventory?.inventoryName || "Selected inventory"}</span>
              </div>
              <p className="fs3 lh5">
                Enter your six-digit POS PIN to verify access to this inventory register.
                Your current basket and register session will remain protected.
              </p>
            </div>
            <div className="pointOfSaleAuth fx-ac fx-jc space1">
              {pin?.map((digit, index) => (
                <input
                  key={index}
                  onKeyPress={handleKeyPress}
                  type="password"
                  value={digit}
                  maxLength={1}
                  ref={(el) => (inputRefs.current[index] = el)}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="pinInput"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-label={`PIN digit ${index + 1}`}
                />
              ))}
            </div>
            <div className="fx-ac space3 fx-jc">
              <button aria-label="Back to inventories" onClick={() => { setPin(["", "", "", "", "", ""]); seCurrentPage(""); }}>
                <KeyboardBackspaceIcon fontSize="large" />
              </button>
              <button disabled={loading || pin.join("").length !== 6} onClick={() => apiFetchItems()}>{loading ? "Verifying…" : "Unlock Register"}</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="pointOfSaleInventorySelection pos-saas-login">
          <button className="closeTerminalLogin" title="Close terminal tab" aria-label="Close terminal tab" onClick={() => window.close()}>
            <KeyboardBackspaceIcon fontSize="large" />
          </button>
          <div className="pos-login-hero">
            <span className="pos-login-eyebrow">UNIVERSE INVENTORY · POINT OF SALE</span>
            <h1>Choose your workspace</h1>
            <p>Select an authorized inventory to open or resume your secure register.</p>
            <div className="pos-login-meta">{companyData?.businessProfile?.basicInfo?.businessName || "Company workspace"} · {inventoriesPayload.length} {inventoriesPayload.length === 1 ? "inventory" : "inventories"}</div>
          </div>
          {lastRegisterClosure?.clientId === companyData?.clientId && <section className="pos-last-register" aria-label="Previous register closure">
            <div><strong>Previous register closed successfully</strong><p>Register {lastRegisterClosure.registerId} · {lastRegisterClosure.inventoryId}</p></div>
            <div className="pos-last-register-values"><span>Sales <strong>₦{Number(lastRegisterClosure.summary?.sales || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</strong></span><span>Cash <strong>₦{Number(lastRegisterClosure.summary?.cash || 0).toLocaleString("en-NG", {minimumFractionDigits:2})}</strong></span>{lastRegisterClosure.cashDifference != null && <span>Cash variance <strong>₦{Number(lastRegisterClosure.cashDifference).toLocaleString("en-NG", {minimumFractionDigits:2})}</strong></span>}</div>
          </section>}
          {inventoryError && <div className="pos-login-error" role="alert">{inventoryError} <button onClick={apiFetchInventories} disabled={loading}>Retry</button></div>}
          {!loading && !inventoryError && inventoriesPayload.length === 0 && <div className="pos-login-empty">No inventories are available for this company. Create or request access to an inventory before using the POS.</div>}
          <div className="inventoriesmainCardGrid g g3 space2">
            {inventoriesPayload?.map((inventory, index) => (
              <figure
                className="inventoriesItemCard fx-cl"
                key={inventory._id || index}
              >
                <div className="rptItemCardTop fx-cl space2">
                  <span className="fs3 fw500">
                    <PointOfSaleIcon style={{ fontSize: "1.6rem" }} />
                    {inventory.inventoryName}
                  </span>

                  <div className="fx-cl spacem">
                    <div className="fx-ac space1">
                      <h3 className="fs5 fw600">{inventory.inventoryType}</h3>

                      <span className="fs1">{inventory.status}</span>
                    </div>

                    <p className="fw500" style={{ fontSize: "1.4rem" }}>
                      {inventory.description}
                    </p>
                  </div>

                 <button
  onClick={() => {
    setSelectedInventory(inventory);
    setPin(["", "", "", "", "", ""]);
    seCurrentPage("pin-page");
  }}
>
  Open Inventory
</button>
                </div>

                <div className="rptItemCardBottom fx-cl spacem fs1">
                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>Branch ID: {inventory.branchId}</span>
                  </div>

                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>Items: {inventory.statistics?.items}</span>
                  </div>

                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>Categories: {inventory.statistics?.categories}</span>
                  </div>

                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>
                      Transactions: {inventory.statistics?.transactions}
                    </span>
                  </div>

                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>
                      Sales:{" "}
                      {inventory.settings?.allowSales ? "Enabled" : "Disabled"}
                    </span>
                  </div>

                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>
                      Barcode Scanning:{" "}
                      {inventory.settings?.barcodeScanning
                        ? "Enabled"
                        : "Disabled"}
                    </span>
                  </div>

                  <div className="fx-ac spacem">
                    <CheckCircleOutlinedIcon fontSize="large" />
                    <span>
                      QR Scanning:{" "}
                      {inventory.settings?.qrScanning ? "Enabled" : "Disabled"}
                    </span>
                  </div>

                  {inventory.isDefault && (
                    <div className="fx-ac spacem">
                      <CheckCircleOutlinedIcon
                        fontSize="large"
                        style={{ color: "#22c55e" }}
                      />
                      <span>Default Inventory</span>
                    </div>
                  )}
                </div>
              </figure>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
